import { FOCUSABLE_SELECTOR, NextTrapTarget } from './focus-trap';

/**
 * The modal scaffolding the phone drawer and the phone filter sheet share (P/Member_Hub.html:517-521, 603-620, 1020):
 *
 * - on `Activate`, the control that had focus is remembered, `body` and the nearest vertically scrolling ancestor of the host (the
 *   surface's scrollport, `mj-agu-surface`) are locked, a document-level Tab listener starts trapping focus in the panel (focus that has
 *   strayed outside is pulled back in; an empty panel holds focus on itself), and focus moves to the initial control on the next task —
 *   after the render that drops `inert`, which an element must lose before it can take focus;
 * - on `Deactivate`, all of that is undone and focus goes back to the opener.
 *
 * Held by composition on each component; the component calls `Deactivate` from ngOnDestroy so no listener or lock outlives it.
 */
export class ModalFocusController {
    private opener: HTMLElement | null = null;
    private lockedBody: string | null = null;
    private lockedScrollport: { Element: HTMLElement; OverflowY: string } | null = null;
    private focusTimer: ReturnType<typeof setTimeout> | null = null;
    private listening = false;
    private readonly onDocumentKeydown = (event: KeyboardEvent): void => this.OnKeydown(event);

    constructor(
        private readonly document: Document,
        /** The dialog element the trap keeps focus in. */
        private readonly panel: () => HTMLElement | null,
        /** Where focus lands on open. */
        private readonly initialFocus: () => HTMLElement | null
    ) {}

    /** Whether the modal is active (its trap and locks are held). */
    get Active(): boolean {
        return this.listening;
    }

    /** `host` is the component's own element, whose scrolling ancestor is locked; `afterFocus` runs right after the initial focus move. */
    Activate(host: HTMLElement | null, afterFocus?: () => void): void {
        if (!this.listening) {
            const active = this.document.activeElement;
            this.opener = active instanceof HTMLElement && active !== this.document.body ? active : null;
            this.document.addEventListener('keydown', this.onDocumentKeydown);
            this.listening = true;
        }
        this.Lock(host);
        this.ClearFocusTimer();
        this.focusTimer = setTimeout(() => {
            this.focusTimer = null;
            this.initialFocus()?.focus();
            afterFocus?.();
        }, 0);
    }

    Deactivate(): void {
        this.ClearFocusTimer();
        if (this.listening) {
            this.document.removeEventListener('keydown', this.onDocumentKeydown);
            this.listening = false;
        }
        this.Unlock();
        const opener = this.opener;
        this.opener = null;
        if (opener && opener.isConnected) opener.focus();
    }

    /** Tab and Shift+Tab stay inside the active panel (`NextTrapTarget`). */
    OnKeydown(event: KeyboardEvent): void {
        const panel = this.panel();
        if (event.key !== 'Tab' || !this.listening || !panel) return;
        const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusables.length === 0) {
            event.preventDefault();
            panel.focus();
            return;
        }
        const active = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
        const target = NextTrapTarget(focusables, active, event.shiftKey);
        if (target === null) return;
        event.preventDefault();
        target.focus();
    }

    private Lock(host: HTMLElement | null): void {
        if (this.lockedBody === null) {
            this.lockedBody = this.document.body.style.overflow;
            this.document.body.style.overflow = 'hidden';
        }
        if (this.lockedScrollport === null) {
            const scrollport = this.ScrollportOf(host);
            if (scrollport) {
                this.lockedScrollport = { Element: scrollport, OverflowY: scrollport.style.overflowY };
                scrollport.style.overflowY = 'hidden';
            }
        }
    }

    private Unlock(): void {
        if (this.lockedBody !== null) {
            this.document.body.style.overflow = this.lockedBody;
            this.lockedBody = null;
        }
        if (this.lockedScrollport !== null) {
            this.lockedScrollport.Element.style.overflowY = this.lockedScrollport.OverflowY;
            this.lockedScrollport = null;
        }
    }

    /**
     * The nearest ancestor of `host` that can scroll vertically (`overflow-y` auto|scroll) AND currently overflows
     * (`scrollHeight > clientHeight`), below `body`. An ancestor that is merely scrollable by style but never overflows (MJExplorer's
     * `mj-page-body`) is not the real scrollport, so it is skipped; when no ancestor overflows, the first scrollable-by-style ancestor
     * is returned instead.
     */
    private ScrollportOf(host: HTMLElement | null): HTMLElement | null {
        const view = this.document.defaultView;
        let firstScrollable: HTMLElement | null = null;
        let element = host?.parentElement ?? null;
        while (element && element !== this.document.body) {
            const overflowY = view ? view.getComputedStyle(element).overflowY : element.style.overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                if (element.scrollHeight > element.clientHeight) return element;
                if (firstScrollable === null) firstScrollable = element;
            }
            element = element.parentElement;
        }
        return firstScrollable;
    }

    private ClearFocusTimer(): void {
        if (this.focusTimer !== null) {
            clearTimeout(this.focusTimer);
            this.focusTimer = null;
        }
    }
}
